import { useEffect, useMemo, useState } from "react";
import { Grid, Stack, Button, SvgIcon } from "@mui/material";
import {
  keepPreviousData,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import SurnameInput from "../utils/medicalDocument/form/patient/SurnameInput";
import PatientAutocomplete from "../utils/patient/search/Autocomplete";
import usePatientPickerForm, {
  defaultPatient,
} from "../utils/patient/search/hooks/usePatientPickerForm";
import useIsAsyncPatientSearch from "../utils/patient/search/hooks/useIsAsyncPatientSearch";
import usePatientSearchStrategy from "../utils/patient/search/hooks/usePatientSearchStrategy";
import { ErrorAlert } from "../../utils";
import { useApi, useIsInQuickLink, useNavigate } from "../../utils/hooks";
import { useAuth } from "../../auth/context";
import TitleHeadSess from "../../utils/TitleHeadSess";
import PatientsTable from "./table-list";
import {
  PersonAddAlt1 as PatientIcon,
  Share as ShareIcon,
} from "@mui/icons-material";

const SyncPatientPicker = ({ setPatient, allPatients, loading }) => {
  const form = usePatientPickerForm(setPatient);

  return (
    <SurnameInput
      form={form}
      patients={allPatients}
      helperText=""
      loading={loading}
      label={"Buscar pacientes"}
      noRequired={true}
    />
  );
};

const AsyncPatientPicker = ({ setPatient }) => {
  const form = usePatientPickerForm(setPatient);

  return (
    <PatientAutocomplete
      form={form}
      helperText=""
      label={"Buscar pacientes"}
      noRequired={true}
    />
  );
};

const PageLayout = ({ picker, table }) => {
  const navigate = useNavigate();

  const actions = [
    {
      variant: "contained",
      icon: <PatientIcon />,
      name: "Agregar paciente",
      action: () => navigate("/patients/new", { state: { url: "/patients" } }),
    },
    {
      variant: "outlined",
      icon: <ShareIcon />,
      name: "Link pacientes",
      action: () =>
        navigate("/patients/new/link-self-registration", {
          state: { url: "/patients" },
        }),
    },
  ];

  const RenderButtons = () => {
    const isInQuickLink = useIsInQuickLink();
    if (isInQuickLink) return null;

    return actions.map(({ name, variant, icon, action }) => (
      <Button
        key={name}
        variant={variant}
        sx={{
          mr: { xs: 0, sm: 1, md: 1 },
          ml: { xs: 0, sm: 1, md: 1 },
          fontSize: { xs: 12, sm: "auto" },
        }}
        onClick={action}
      >
        <SvgIcon sx={{ fontSize: 18, mr: 0.5 }}>{icon}</SvgIcon>
        {name}
      </Button>
    ));
  };

  return (
    <Stack
      spacing="1rem"
      sx={{ p: { xs: "1rem", sm: "2rem" }, mt: { xs: "1rem" }, width: "100%" }}
    >
      <TitleHeadSess title={"Pacientes"} urlRedirect={"/"} />
      <Grid container alignItems="center">
        <Grid item xs={12} sm={12} md={6} lg={8}>
          {picker}
        </Grid>
        <Grid
          item
          xs={12}
          sm={12}
          md={6}
          lg={4}
          container
          sx={{
            display: "flex",
            flexDirection: "row",
            justifyContent: { xs: "space-between", sm: "start", md: "end" },
            mt: { xs: 2, sm: 2, md: 0 },
          }}
        >
          <RenderButtons />
        </Grid>
      </Grid>
      {table}
    </Stack>
  );
};

const ROWS_PER_PAGE = 10;

const SyncPatientsView = () => {
  const [patient, setPatient] = useState({});
  const [patients, setPatients] = useState([]);
  const {
    strategyProps: { patients: allPatients, loading },
    loadErrorAlertProps,
  } = usePatientSearchStrategy();
  const [page, setPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  useEffect(() => {
    if (patient.id) {
      setPage(0);
      const searchPatient = allPatients.filter((pat) => pat.id === patient.id);
      setPatients(searchPatient);
      setTotalItems(searchPatient.length);
    } else {
      const offset = page * ROWS_PER_PAGE;
      const paginatedPatients = allPatients.slice(offset, offset + ROWS_PER_PAGE);
      setTotalItems(allPatients.length);
      setPatients(paginatedPatients);
    }
  }, [patient, allPatients, page]);

  return (
    <>
      <PageLayout
        picker={
          <SyncPatientPicker
            setPatient={setPatient}
            allPatients={allPatients}
            loading={loading}
          />
        }
        table={
          <PatientsTable
            patients={patients}
            loading={loading}
            page={page}
            rowsPerPage={ROWS_PER_PAGE}
            setPage={setPage}
            count={totalItems}
          />
        }
      />
      <ErrorAlert {...loadErrorAlertProps} />
    </>
  );
};

const AsyncPatientsView = () => {
  const api = useApi();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [patient, setPatient] = useState(defaultPatient);
  const [page, setPage] = useState(0);
  const [sortingField, setSortingField] = useState(null);

  const hasPickedPatient = Boolean(patient.id);

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["patients", "list"] });
  }, []);

  const queryParams = useMemo(() => {
    const params = { page: page + 1, limit: ROWS_PER_PAGE };
    if (sortingField?.id) {
      params.sortBy = `${sortingField.id}:${sortingField.direction.toUpperCase()}`;
    }
    return params;
  }, [page, sortingField]);

  const { isLoading: loading, data } = useQuery({
    queryKey: ["patients", "list", queryParams],
    queryFn: () => api.patients(queryParams),
    enabled: !hasPickedPatient,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  });

  const { patients, totalItems } = useMemo(() => {
    if (hasPickedPatient) {
      const single = user.filterPatients([patient]);
      return { patients: single, totalItems: single.length };
    }
    const items = user.filterPatients(data?.data ?? []);
    return { patients: items, totalItems: data?.meta?.totalItems ?? 0 };
  }, [hasPickedPatient, patient, data, user]);

  useEffect(() => {
    if (hasPickedPatient) setPage(0);
  }, [hasPickedPatient]);

  const handleRequestSort = (newSortField) => {
    setSortingField(newSortField);
    setPage(0);
  };

  return (
    <PageLayout
      picker={<AsyncPatientPicker setPatient={setPatient} />}
      table={
        <PatientsTable
          patients={patients}
          loading={loading && !hasPickedPatient}
          page={page}
          rowsPerPage={ROWS_PER_PAGE}
          setPage={setPage}
          count={totalItems}
          handleRequestSort={handleRequestSort}
        />
      }
    />
  );
};

const Patients = () => {
  const isAsync = useIsAsyncPatientSearch();
  return isAsync ? <AsyncPatientsView /> : <SyncPatientsView />;
};

export default Patients;
